import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

const Hero = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const campaignSlides = [
    {
      id: 1,
      image: "/assets/donatepe3.jpeg",
      category: "Medical",
      categoryColor: "from-rose-500 to-red-500",
      title: "Help Save Lives with Medical Fundraising",
    },
    {
      id: 2,
      image: "/assets/donatepe2.jpeg",
      category: "Hunger Relief",
      categoryColor: "from-red-500 to-rose-500",
      title: "Support Hunger Relief Campaigns",
    },
    {
      id: 3,
      image: "/assets/donatepe1.jpeg",
      category: "Hunger Relief",
      categoryColor: "from-red-500 to-rose-500",
      title: "Feed Families in Need",
    },
    {
      id: 4,
      image: "/assets/education_hero.jpeg",
      category: "Education",
      categoryColor: "from-blue-500 to-indigo-500",
      title: "Empower Education for Children",
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % campaignSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleStartFundraiser = () => {
    localStorage.removeItem("preselectedPurpose");
    navigate("/start-fundraiser");
  };

  return (
    <section className="relative bg-white pt-8 md:pt-12 overflow-hidden">
      <div className="absolute inset-0 bg-white"></div>
      
      <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
        
        <div className="grid lg:grid-cols-12 gap-4 lg:gap-8 items-center w-full">
          
          {/* CAROUSEL */}
          <div className="lg:col-span-6 lg:order-last flex justify-center">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] lg:aspect-[4/3] w-full max-w-[500px] lg:max-w-none lg:h-[400px]">
              
              {campaignSlides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`absolute inset-0 transition-all duration-1000 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img 
                    src={slide.image}
                    alt={`${slide.category} fundraising on DonatePe`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/30 to-transparent"></div>
                  
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1.5 bg-gradient-to-r ${slide.categoryColor} text-white rounded-full text-xs font-semibold shadow-lg`}>
                      {slide.category}
                    </span>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                    <h3 className="text-base md:text-lg font-bold leading-tight line-clamp-2">
                      {slide.title}
                    </h3>
                  </div>
                </div>
              ))}

              {/* Dots */}
              <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
                {campaignSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`transition-all ${
                      index === currentSlide
                        ? 'w-2 h-2 bg-white rounded-full shadow-lg scale-125'
                        : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80 rounded-full'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* LEFT COLUMN */}
          <div className="lg:col-span-6 space-y-3 md:space-y-4">
            
            <div className="hidden lg:inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border border-gray-100">
              <span className="text-sm font-medium text-gray-700">
                World's most loved fundraising platform
              </span>
            </div>

            {/* UPDATED H1 */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 tracking-tight leading-tight text-center lg:text-left">
              DonatePe - Start a fundraiser.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                It's free, it's easy, it matters.
              </span>
            </h1>

            {/* STATS */}
            <div className="py-2">
              <div className="border border-gray-300 rounded-xl bg-gradient-to-br from-gray-50 to-white shadow-md p-3 w-full">
                <div className="flex items-center justify-between gap-2">
                  
                  <div className="flex-1 flex flex-col items-center">
                    <span className="text-sm md:text-base lg:text-lg font-bold text-emerald-600">3L</span>
                    <span className="text-[8px] md:text-xs text-gray-600">Donors</span>
                  </div>

                  <div className="flex items-center justify-center">
                    <span className="text-gray-400 text-sm md:text-lg">•</span>
                  </div>

                  <div className="flex-1 flex flex-col items-center">
                    <span className="text-xs md:text-sm lg:text-base font-semibold text-teal-600">100%</span>
                    <span className="text-[8px] md:text-xs text-gray-600">impact</span>
                  </div>

                  <div className="flex items-center justify-center">
                    <span className="text-gray-400 text-sm md:text-lg">+</span>
                  </div>

                  <div className="flex-1 flex flex-col items-center">
                    <span className="text-xs md:text-sm lg:text-base font-semibold text-emerald-600">3.54%</span>
                    <span className="text-[8px] md:text-xs text-gray-600">fees</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="hidden lg:block text-base lg:text-lg text-gray-600 leading-relaxed max-w-lg">
              In 2 minutes, you can start raising money for yourself, 
              someone you love, or a cause you believe in.
            </p>

            {/* CTA */}
            <div className="pt-1 flex justify-center lg:justify-start">
              <button
                onClick={handleStartFundraiser}
                className="px-5 py-2.5 md:px-6 md:py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-lg md:rounded-xl font-semibold text-sm md:text-base hover:shadow-xl transition-all w-full sm:w-auto"
              >
                Start your fundraiser →
              </button>
            </div>

            {/* SEO hidden content */}
            <p className="sr-only">
              DonatePe is an online donation and fundraising platform in India 
              that helps individuals raise funds for medical emergencies, 
              education, and social causes easily and securely.
            </p>

          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default Hero;